import type { FiatCashEvent, LedgerEvent } from './types';
import type { FiCsvDiagnosticRow } from './fiDiagnostics';
import type { ParseCryptoComCsvOptions } from './cryptoComCsv';
import { parseCryptoComCsv } from './cryptoComCsv';
import { detectExchangeCsvSchema } from './exchangeCsvDetect';
import {
  parseBinanceSpotTradesCsv,
  parseBinanceTransactionHistoryCsv,
  type BinanceParseStats,
} from './binanceCsv';
import {
  parseCoinbaseTaxesTransactionCsv,
  parseCoinbaseExchangeFillsCsv,
  parseCoinbaseAccountStatementCsv,
  type CoinbaseParseStats,
} from './coinbaseCsv';

/** Join multiple CSV files in the UI; each part is detected and parsed separately. */
export const EXCHANGE_CSV_PART_DELIM = '\n\n__SISUFORGE_CSV_PART__\n';

export function splitCsvImportParts(text: string): string[] {
  const t = text.trim();
  if (!t.includes(EXCHANGE_CSV_PART_DELIM)) return [t];
  return t.split(EXCHANGE_CSV_PART_DELIM).map((p) => p.trim()).filter(Boolean);
}

export type ExchangeCsvBlockSummary = {
  partIndex: number;
  detected_exchange: string;
  detected_schema_family: string;
  detection_score: number;
  events_emitted: number;
  note?: string;
};

export type MultiExchangeParseResult = {
  events: LedgerEvent[];
  skipped: string[];
  diagnostics: FiCsvDiagnosticRow[];
  fiatCashEvents: FiatCashEvent[];
  aborted: boolean;
  blockSummaries: ExchangeCsvBlockSummary[];
};

function fmtBinanceStats(s: BinanceParseStats): string {
  const top = Object.entries(s.rawOperationCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([k, v]) => `${k}:${v}`)
    .join(', ');
  return (
    `rows=${s.rowsTotal} acq=${s.mappedAcquisition} disp=${s.mappedDisposal} inc=${s.mappedIncome} int=${s.mappedInternal} unmapped=${s.unmapped} empty=${s.skippedEmpty}` +
    (top ? ` | top_ops ${top}` : '')
  );
}

function fmtCoinbaseStats(s: CoinbaseParseStats): string {
  const top = Object.entries(s.rawTypeCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([k, v]) => `${k}:${v}`)
    .join(', ');
  return (
    `rows=${s.rowsTotal} acq=${s.mappedAcquisition} disp=${s.mappedDisposal} inc=${s.mappedIncome} int=${s.mappedInternal} unmapped=${s.unmapped}` +
    (top ? ` | top_types ${top}` : '')
  );
}

/**
 * Parse one CSV text block: schema detection → Binance / Coinbase handlers or Crypto.com fallback.
 */
export async function parseExchangeCsvBlock(
  text: string,
  options: ParseCryptoComCsvOptions & { filenameHint?: string }
): Promise<{
  events: LedgerEvent[];
  skipped: string[];
  diagnostics: FiCsvDiagnosticRow[];
  fiatCashEvents: FiatCashEvent[];
  aborted: boolean;
  detection: ReturnType<typeof detectExchangeCsvSchema>;
  summaryLine: string;
}> {
  const det = detectExchangeCsvSchema(text, options.filenameHint);
  const { signal, onProgress, yieldEvery } = options;

  const baseOpts = { signal, onProgress, yieldEvery };

  if (det.exchange === 'cdc' && det.schemaFamily === 'crypto_com_transaction_export') {
    const r = await parseCryptoComCsv(text, { signal, onProgress, yieldEvery });
    return {
      ...r,
      detection: det,
      summaryLine: `[cdc:${det.schemaFamily}] events=${r.events.length} fiat_cash=${r.fiatCashEvents.length}`,
    };
  }

  if (det.exchange === 'binance' && det.schemaFamily === 'binance_spot_trade_history') {
    const r = await parseBinanceSpotTradesCsv(text, baseOpts);
    return {
      events: r.events,
      skipped: r.skipped,
      diagnostics: r.diagnostics,
      fiatCashEvents: [],
      aborted: !!signal?.aborted,
      detection: det,
      summaryLine: `[binance:spot] ${fmtBinanceStats(r.stats)}`,
    };
  }

  if (det.exchange === 'binance' && det.schemaFamily === 'binance_transaction_history') {
    const r = await parseBinanceTransactionHistoryCsv(text, baseOpts);
    return {
      events: r.events,
      skipped: r.skipped,
      diagnostics: r.diagnostics,
      fiatCashEvents: [],
      aborted: !!signal?.aborted,
      detection: det,
      summaryLine: `[binance:tx] ${fmtBinanceStats(r.stats)}`,
    };
  }

  if (det.exchange === 'coinbase' && det.schemaFamily === 'coinbase_taxes_transaction_report') {
    const r = await parseCoinbaseTaxesTransactionCsv(text, baseOpts);
    return {
      events: r.events,
      skipped: r.skipped,
      diagnostics: r.diagnostics,
      fiatCashEvents: [],
      aborted: !!signal?.aborted,
      detection: det,
      summaryLine: `[coinbase:taxes] ${fmtCoinbaseStats(r.stats)}`,
    };
  }

  if (det.exchange === 'coinbase' && det.schemaFamily === 'coinbase_exchange_fills') {
    const r = await parseCoinbaseExchangeFillsCsv(text, baseOpts);
    return {
      events: r.events,
      skipped: r.skipped,
      diagnostics: r.diagnostics,
      fiatCashEvents: [],
      aborted: !!signal?.aborted,
      detection: det,
      summaryLine: `[coinbase:fills] ${fmtCoinbaseStats(r.stats)}`,
    };
  }

  if (det.exchange === 'coinbase' && det.schemaFamily === 'coinbase_account_statement') {
    const r = parseCoinbaseAccountStatementCsv(text);
    return {
      events: r.events,
      skipped: r.skipped,
      diagnostics: r.diagnostics,
      fiatCashEvents: [],
      aborted: false,
      detection: det,
      summaryLine: `[coinbase:account_statement] diagnostic_only rows=${r.stats.rowsTotal}`,
    };
  }

  if (det.exchange === 'unknown') {
    onProgress?.('Schema unknown from headers — trying Crypto.com parser as fallback…');
    const r = await parseCryptoComCsv(text, { signal, onProgress, yieldEvery });
    const fb =
      r.events.length > 0
        ? `[fallback:cdc] matched as Crypto.com (${r.events.length} events)`
        : `[fallback:cdc] no Crypto.com match — check export type (detection score was ${det.score})`;
    return {
      ...r,
      detection: det,
      summaryLine: fb,
      skipped:
        r.events.length > 0
          ? r.skipped
          : [
              ...r.skipped,
              'Could not detect Binance/Coinbase/Crypto.com schema from headers — see diagnostics.',
            ],
    };
  }

  const r = await parseCryptoComCsv(text, { signal, onProgress, yieldEvery });
  return {
    ...r,
    detection: det,
    summaryLine: `[fallback:cdc] detection=${det.exchange}/${det.schemaFamily} → Crypto.com parse (${r.events.length} events)`,
  };
}

export type ParseMultiExchangeCsvOptions = ParseCryptoComCsvOptions & {
  /** Per-part filename hints in the same order as `splitCsvImportParts` (optional). */
  partFilenameHints?: string[];
};

/**
 * Split merged textarea / multi-file payload, detect each part, merge ledger events + fiat cash.
 */
export async function parseMultiExchangeCsvImport(
  fullText: string,
  options?: ParseMultiExchangeCsvOptions
): Promise<MultiExchangeParseResult> {
  const parts = splitCsvImportParts(fullText);
  const hints = options?.partFilenameHints ?? [];
  const events: LedgerEvent[] = [];
  const skipped: string[] = [];
  const diagnostics: FiCsvDiagnosticRow[] = [];
  const fiatCashEvents: FiatCashEvent[] = [];
  const blockSummaries: ExchangeCsvBlockSummary[] = [];
  let aborted = false;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]!;
    const hint = hints[i];
    const r = await parseExchangeCsvBlock(part, {
      ...options,
      filenameHint: hint,
    });
    events.push(...r.events);
    skipped.push(`— Part ${i + 1}/${parts.length}: ${r.summaryLine}`);
    skipped.push(...r.skipped);
    diagnostics.push(...r.diagnostics);
    fiatCashEvents.push(...r.fiatCashEvents);
    if (r.aborted) aborted = true;
    blockSummaries.push({
      partIndex: i,
      detected_exchange: r.detection.exchange,
      detected_schema_family: r.detection.schemaFamily,
      detection_score: r.detection.score,
      events_emitted: r.events.length,
    });
  }

  return { events, skipped, diagnostics, fiatCashEvents, aborted, blockSummaries };
}
